import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { PasswordField } from "../components/PasswordField";

// Settings (Part N, R1-OD-83..87), opened as the same side panel every desk selection uses. You,
// sign-in & security state, and notifications. Frequency is framed as how often Juno INTERRUPTS you,
// never how often it works — the agents are always live and never batch. Email/mobile change and
// password change are auth-bearing flows and are shown as state, not edited here yet.

export function Settings({ onClose }) {
  const { authedFetch, endSession } = useAuth();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwHide, setPwHide] = useState(0);   // bumped on submit: the fields go back to hidden
  const [dest, setDest] = useState({ kind: "email", value: "" });
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const [secMsg, setSecMsg] = useState("");

  const load = () =>
    authedFetch("/owner/settings")
      .then((d) => { setS(d); setName(d.you.name || ""); setCompany(d.you.company || ""); })
      .catch((e) => setErr(e.detail || "Could not load your settings."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (m) => { setSavedNote(m); setTimeout(() => setSavedNote(""), 2200); };

  const saveProfile = async () => {
    try {
      await authedFetch("/owner/commands/settings/profile", { method: "POST", body: { name, company } });
      flash("Saved."); load();
    } catch (e) { setErr(e.detail || "Could not save."); }
  };

  const toggleEmail = async () => {
    const next = !s.notifications.email_channel;
    setS((p) => ({ ...p, notifications: { ...p.notifications, email_channel: next } }));
    try { await authedFetch("/owner/commands/settings/notifications", { method: "POST", body: { email_channel: next } }); flash("Saved."); }
    catch (e) { setErr(e.detail || "Could not save."); load(); }
  };

  const changePassword = async () => {
    setSecMsg("");
    setPwHide((n) => n + 1);
    try {
      await authedFetch("/owner/commands/settings/password", { method: "POST", body: pw });
      endSession("Your password was changed and every other session was signed out. Sign in with your new password. If this wasn't you, contact support@junocre.com right away.");
    } catch (e) { setSecMsg(e.detail || "Could not change your password."); }
  };
  const sendDestCode = async () => {
    setSecMsg("");
    try {
      const r = await authedFetch("/owner/commands/settings/destination/challenge", { method: "POST", body: dest });
      setChallenge(r); setSecMsg("Code sent — enter it below to confirm.");
    } catch (e) { setSecMsg(e.detail || "Could not send a code."); }
  };
  const confirmDest = async () => {
    setSecMsg("");
    try {
      await authedFetch("/owner/commands/settings/destination/confirm", { method: "POST", body: { challenge_id: challenge.challenge_id, code } });
      setChallenge(null); setCode(""); setDest({ kind: "email", value: "" }); flash("Updated."); load();
    } catch (e) { setSecMsg(e.detail || "That code did not work."); }
  };

  return (
    <div className="panel-scrim" data-testid="settings-panel" onClick={onClose}>
      <aside className="panel" onClick={(e) => e.stopPropagation()}>
        <div className="panel-top">
          <div className="panel-ref">Settings</div>
          <button className="panel-close" data-testid="settings-close" onClick={onClose}>Close</button>
        </div>
        {err && <div className="panel-err" data-testid="settings-err">{err}</div>}
        {!s && !err && <div className="panel-loading">Loading…</div>}
        {s && (
          <>
            <section className="panel-section" data-testid="settings-you">
              <h3 className="panel-h">You</h3>
              <label className="set-label" htmlFor="set-name">Name</label>
              <input id="set-name" className="set-input" data-testid="settings-name"
                     value={name} onChange={(e) => setName(e.target.value)} />
              <label className="set-label" htmlFor="set-company">Company <span className="set-opt">optional</span></label>
              <input id="set-company" className="set-input" data-testid="settings-company"
                     value={company} onChange={(e) => setCompany(e.target.value)} placeholder="—" />
              <div className="set-readonly">
                <div><span className="set-ro-k">Email</span><span className="set-ro-v" data-testid="settings-email">{s.you.email || "—"}</span></div>
                {s.you.has_mobile && (
                  <div><span className="set-ro-k">Mobile</span><span className="set-ro-v" data-testid="settings-mobile">{s.you.mobile_masked}</span></div>
                )}
              </div>
              <p className="set-note">{s.you.note}</p>
              <button className="chrome-btn chrome-btn-primary" data-testid="settings-save-profile" onClick={saveProfile}>Save</button>
              {savedNote && <span className="set-saved" data-testid="settings-saved">{savedNote}</span>}
            </section>

            <section className="panel-section" data-testid="settings-security">
              <h3 className="panel-h">Sign-in &amp; security</h3>
              <div className="set-readonly">
                <div><span className="set-ro-k">Sign-in</span><span className="set-ro-v">{s.security.sign_in_method}</span></div>
                <div><span className="set-ro-k">Sessions</span><span className="set-ro-v">One active session at a time</span></div>
              </div>
              <p className="set-note">{s.security.note}</p>

              <label className="set-label">Change password</label>
              <PasswordField className="set-input" testid="settings-current-pw" placeholder="Current password"
                     hideOn={pwHide} autoComplete="current-password"
                     value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} />
              <div style={{ marginTop: 8 }}>
                <PasswordField className="set-input" testid="settings-new-pw" placeholder="New password"
                     hideOn={pwHide} autoComplete="new-password"
                     value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
              </div>
              <button className="chrome-btn" style={{ marginTop: 10 }} data-testid="settings-change-pw"
                      disabled={!pw.current_password || !pw.new_password} onClick={changePassword}>Change password</button>

              <label className="set-label">{s.you.has_mobile ? "Change email or mobile" : "Change email"}</label>
              {s.you.has_mobile && (
                <div className="set-channels">
                  <button className={"set-toggle" + (dest.kind === "email" ? " set-toggle-on" : "")}
                          data-testid="settings-dest-email" onClick={() => setDest({ kind: "email", value: "" })}>Email</button>
                  <button className={"set-toggle" + (dest.kind === "mobile" ? " set-toggle-on" : "")}
                          data-testid="settings-dest-mobile" onClick={() => setDest({ kind: "mobile", value: "" })}>Mobile</button>
                </div>
              )}
              <input className="set-input" style={{ marginTop: 8 }} data-testid="settings-dest-value"
                     placeholder={dest.kind === "email" ? "new@example.com" : "+15551234567"}
                     value={dest.value} onChange={(e) => setDest({ ...dest, value: e.target.value })} />
              {!challenge ? (
                <button className="chrome-btn" style={{ marginTop: 10 }} data-testid="settings-dest-send"
                        disabled={!dest.value} onClick={sendDestCode}>Send verification code</button>
              ) : (
                <>
                  <input className="set-input" style={{ marginTop: 8 }} data-testid="settings-dest-code"
                         inputMode="numeric" placeholder="Verification code" value={code}
                         onChange={(e) => setCode(e.target.value)} />
                  <button className="chrome-btn" style={{ marginTop: 10 }} data-testid="settings-dest-confirm"
                          disabled={!code} onClick={confirmDest}>Confirm new {challenge.kind}</button>
                </>
              )}
              {secMsg && <p className="set-note" data-testid="settings-sec-msg">{secMsg}</p>}
            </section>

            <section className="panel-section" data-testid="settings-notifications">
              <h3 className="panel-h">Notifications</h3>
              {/* Frequency control HIDDEN in R1 (founder gate 20 Sep): R1 sends almost nothing that is
                  not a decision, and decisions notify at t=0 in-desk regardless of frequency. A control
                  that governs nothing is a promise we would not keep. Restore this block the moment a
                  non-decision notification stream and its dispatch gate exist. */}
              <p className="set-framing" data-testid="settings-framing">
                Juno tells you the moment a decision needs you, in the desk. It does not batch and it
                does not send marketing — there is nothing here to turn down yet.</p>
              <div className="set-channels">
                <button className={"set-toggle" + (s.notifications.email_channel ? " set-toggle-on" : "")}
                        data-testid="settings-email-toggle" onClick={toggleEmail}>
                  Email {s.notifications.email_channel ? "on" : "off"}
                </button>
                <span className="set-channel-fixed">In-desk · always on</span>
                <span className="set-channel-fixed">Text · sign-in codes only</span>
              </div>
              <p className="set-note">{s.notifications.channels_note}</p>
            </section>
          </>
        )}
      </aside>
    </div>
  );
}
